/* eslint-disable react-hooks/exhaustive-deps */
import axios from 'axios';
import { History } from 'history';
import moment from 'moment-timezone';
import queryString from 'query-string';
import { useEffect, useMemo } from 'react';
import BookingAPI from '../api/booking.api';
import { PaymentType } from '../enums/PaymentType';
import URLBuilder from '../utils/URLBuilder';
import { useRootStoreContext } from './RootStore';

/*
 * This store determines the URL of the application when in the Booking Process flow
 */

export namespace BookingProcessStoreNS {
    export type Store = {
        startBookingProcess: (
            restaurantId: number,
            restaurantSlug: string,
            dateTime: string,
            guests: number,
            eventId: number | undefined,
            isWaitingList: boolean | undefined,
            isWaitingListWhenFullyBooked: boolean | undefined,
        ) => Promise<boolean>;
        nextStep: (historyAction?: 'Push' | 'Replace', guid?: string) => void;
        error: () => void;
        stopBookingProcess: () => void;
    };

    export enum Type {
        Reserve = 0,
        Complete = 1,
        Confirmation = 2,
        Upsell = 5,
    }

    export type Step = {
        type: Type;
    };

    export type StringType = 'reserve' | 'complete' | 'confirmation' | 'upsell';

    export interface Menu {
        id: number;
        name: string;
        shortDescription: string;
        description: string;
        paymentType: PaymentType;
        paymentAmount: number | undefined;
        isPublished: boolean;
    }

    export type Data = {
        restaurantId: number | undefined;
        restaurantSlug: string | undefined;
        dateTime: string | undefined;
        guests: number | undefined;
        eventId: number | undefined;
        isWaitingList: boolean | undefined;
        isWaitingListWhenFullyBooked: boolean | undefined;
        guid: string | undefined;
        currentStep: Step | undefined;
        remainingSteps: Step[];
    };

    export type QueryParams = {
        restaurantId: string | undefined;
        dateTime: string | undefined;
        guests: string | undefined;
        eventId: string | undefined;
        isWaitingList: boolean | undefined;
        isWaitingListWhenFullyBooked: boolean | undefined;
        guid: string | undefined;
        currentStep: StringType | undefined;
        isolation: 'true' | undefined;
    };
}

const bookingApi = new BookingAPI();

let bookingProcessData: BookingProcessStoreNS.Data = {
    restaurantId: undefined,
    restaurantSlug: undefined,
    dateTime: undefined,
    guests: undefined,
    eventId: undefined,
    isWaitingList: false,
    isWaitingListWhenFullyBooked: false,
    guid: undefined,
    currentStep: undefined,
    remainingSteps: [],
};

/* HELPER FUNCTIONS */

const setBookingProcessData = (data: BookingProcessStoreNS.Data) => {
    bookingProcessData = {
        ...data,
    };
};

const stopBookingProcess = () => {
    setBookingProcessData({
        restaurantId: undefined,
        restaurantSlug: undefined,
        dateTime: undefined,
        guests: undefined,
        eventId: undefined,
        isWaitingList: undefined,
        isWaitingListWhenFullyBooked: undefined,
        guid: undefined,
        currentStep: undefined,
        remainingSteps: [],
    });
};

// Maps from query string value to the number enum value
const mapQSToStepType = (
    val: BookingProcessStoreNS.StringType,
): BookingProcessStoreNS.Type => {
    switch (val) {
        case 'reserve':
            return BookingProcessStoreNS.Type.Reserve;
        case 'complete':
            return BookingProcessStoreNS.Type.Complete;
        case 'confirmation':
            return BookingProcessStoreNS.Type.Confirmation;
        case 'upsell':
            return BookingProcessStoreNS.Type.Upsell;
        default:
            throw new Error(`Booking process type not found. ${val}`);
    }
};

/*-----------------*/

const useBookingProcessStore = (
    history: History,
): BookingProcessStoreNS.Store => {
    // This store makes use of the UIStore to pop up a modal and to determine isolation mode
    const {
        UIStore: {
            reducer: [uiStoreState],
        },
    } = useRootStoreContext();

    // URL helper for changing the url of the page
    const url = useMemo(() => new URLBuilder(history), []);

    /*
     * NAVIGATES TO ERROR PAGE IF ERROR OCCURS IN THE BOOKING PROCESS
     */
    const error = () => {
        url.updateUrl(
            '/book/reservation-unsuccessful',
            {
                restaurantId: bookingProcessData.restaurantId!,
                restaurantSlug: bookingProcessData.restaurantSlug,
            },
            'Replace',
        );
    };

    /*
     * FETCHES THE ORDER OF STEP NEEDED TO TAKE IN THE BOOKING PROCESS FROM THE API
     */
    const fetchServerData = () => {
        return new Promise<{
            bookingProcessSteps: BookingProcessStoreNS.Type[];
        }>((resolve, reject) => {
            if (!bookingProcessData.restaurantId) {
                reject();
            }
            const cancelToken = axios.CancelToken.source();
            bookingApi
                .getBookingProcessOrder(
                    bookingProcessData.restaurantId!,
                    bookingProcessData.eventId ?? null,
                    !!bookingProcessData.isWaitingList,
                    bookingProcessData.dateTime ?? new Date().toISOString(),
                    cancelToken,
                )
                .then((res) => resolve(res.data))
                .catch(() => reject());
        });
    };

    /*
+ SET THE BOOKING PROCESS IN THE SAME STATE IF THE BROWSER REFRESHES / RE-HYDRATE
*/
    useEffect(() => {
        // @ts-ignore
        const {
            restaurantId,
            dateTime,
            guests,
            eventId,
            guid,
            currentStep,
            isWaitingList,
            isWaitingListWhenFullyBooked,
        }: BookingProcessStoreNS.QueryParams = queryString.parse(
            history.location.search,
        );

        if (!currentStep) {
            stopBookingProcess();
            return;
        }

        const stepType = mapQSToStepType(currentStep);
        setBookingProcessData({
            ...bookingProcessData,
            restaurantId: restaurantId ? parseInt(restaurantId, 10) : undefined,
            dateTime,
            guests: guests ? parseInt(guests, 10) : undefined,
            eventId: eventId ? parseInt(eventId, 10) : undefined,
            isWaitingList: isWaitingList ? isWaitingList : undefined,
            isWaitingListWhenFullyBooked: isWaitingListWhenFullyBooked
                ? isWaitingListWhenFullyBooked
                : undefined,
            guid,
            currentStep: { type: stepType },
        });
        fetchServerData()
            .then((data) => {
                const steps = data.bookingProcessSteps;
                let step: BookingProcessStoreNS.Type | undefined;
                do {
                    step = steps.shift();
                } while (
                    steps.length > 0 &&
                    step !== bookingProcessData.currentStep!.type
                );

                // No more steps to perform
                if (steps.length === 0) {
                    stopBookingProcess();
                    return;
                }

                // Set data to the state as it was befofe refresh
                setBookingProcessData({
                    ...bookingProcessData,
                    remainingSteps: steps.map((s) => ({ type: s })),
                });
            })
            .catch(() => error());
    }, []);

    useEffect(() => {
        // @ts-ignore
        const { dateTime, guests }: BookingProcessStoreNS.QueryParams =
            queryString.parse(history.location.search);
        setBookingProcessData({
            ...bookingProcessData,
            ...(dateTime ? { dateTime } : {}),
            ...(guests ? { guests: parseInt(guests, 10) } : {}),
        });
    }, [history.location.search]);

    /*
     * STARTS THE BOOKING PROCESS
     * RETURNS PROMISE SO THAT THE CALLER KNOWS WHEN EXECUTION HAS FINISHED
     */
    const startBookingProcess = (
        restaurantId: number,
        restaurantSlug: string,
        dateTime: string,
        guests: number,
        eventId: number | undefined,
        isWaitingList: boolean | undefined,
        isWaitingListWhenFullyBooked: boolean | undefined,
    ) => {
        // Start with initial data
        stopBookingProcess();

        setBookingProcessData({
            ...bookingProcessData,
            restaurantSlug,
            restaurantId,
            dateTime,
            guests,
            eventId,
            isWaitingList,
            isWaitingListWhenFullyBooked,
        });

        return new Promise<boolean>((resolve) => {
            fetchServerData()
                .then((data) => {
                    setBookingProcessData({
                        ...bookingProcessData,
                        remainingSteps: data.bookingProcessSteps.map((s) => ({
                            type: s,
                        })),
                    });
                    // Push to the history stack when the process starts
                    resolve(true);
                    nextStep('Push');
                })
                .catch(() => error());
        });
    };

    /*
     * GOES TO THE NEXT STEP IN THE BOOKING PROCESS
     */
    const nextStep = (
        historyAction: 'Push' | 'Replace' = 'Replace',
        guidParam?: string,
    ) => {
        const steps = [...bookingProcessData.remainingSteps];
        const newStep = steps.shift();

        if (!newStep) {
            error();
            return;
        }

        setBookingProcessData({
            ...bookingProcessData,
            remainingSteps: steps,
            currentStep: newStep,
            guid:
                !bookingProcessData.guid && guidParam
                    ? guidParam
                    : bookingProcessData.guid,
        });

        const {
            dateTime,
            guests,
            restaurantId,
            eventId,
            guid,
            isWaitingList,
            isWaitingListWhenFullyBooked,
        } = bookingProcessData;

        // Check for user selection in query params until reserve step starts (a booking is created)

        // @ts-ignore
        const { menuSelection }: { menuSelection: string | undefined } =
            queryString.parse(history.location.search);

        switch (newStep.type) {
            case BookingProcessStoreNS.Type.Reserve:
                url.updateUrl(
                    '/book/reserve',
                    {
                        dateTime,
                        guests,
                        restaurantId,
                        currentStep: 'reserve',
                        ...(isWaitingList && { isWaitingList }),
                        ...(isWaitingListWhenFullyBooked && {
                            isWaitingListWhenFullyBooked,
                        }),
                        ...(menuSelection && { menuSelection }),
                        ...(eventId && { eventId }),
                        ...(uiStoreState.isIsolatedMode && { isolation: true }),
                    },
                    historyAction,
                );
                break;
            case BookingProcessStoreNS.Type.Complete:
                if (guid) {
                    const expires = moment()
                        .add(7, 'minutes')
                        .valueOf()
                        .toString();
                    url.updateUrl(
                        '/checkout/complete',
                        {
                            guid,
                            restaurantId,
                            currentStep: 'complete',
                            expires,
                            ...(isWaitingList && { isWaitingList }),
                            ...(isWaitingListWhenFullyBooked && {
                                isWaitingListWhenFullyBooked,
                            }),
                            ...(eventId && { eventId }),
                            ...(uiStoreState.isIsolatedMode && {
                                isolation: true,
                            }),
                        },
                        historyAction,
                    );
                } else {
                    error();
                }
                break;
            case BookingProcessStoreNS.Type.Confirmation:
                if (guid) {
                    url.updateUrl(
                        '/checkout/confirmation',
                        {
                            guid,
                            restaurantId,
                            currentStep: 'confirmation',
                            ...(isWaitingList && { isWaitingList }),
                            ...(isWaitingListWhenFullyBooked && {
                                isWaitingListWhenFullyBooked,
                            }),
                            ...(eventId && { eventId }),
                            ...(uiStoreState.isIsolatedMode && {
                                isolation: true,
                            }),
                        },
                        historyAction,
                    );
                } else {
                    error();
                }
                break;
            case BookingProcessStoreNS.Type.Upsell:
                if (restaurantId && guests) {
                    url.updateUrl(
                        '/checkout/upsell',
                        {
                            restaurantId,
                            dateTime,
                            guests,
                            currentStep: 'upsell',
                            ...(eventId && { eventId }),
                            ...(uiStoreState.isIsolatedMode && {
                                isolation: true,
                            }),
                        },
                        historyAction,
                        true,
                    );
                } else {
                    error();
                }
                break;
            default:
                error();
        }

        if (bookingProcessData.remainingSteps.length === 0) {
            stopBookingProcess();
        }
    };

    return {
        startBookingProcess,
        nextStep,
        error,
        stopBookingProcess,
    };
};

export default useBookingProcessStore;
